import React, {useCallback, useMemo, useState} from 'react';

import {addAndUpdateSteps} from 'analysis-tool/AnalysisActions';
import {getArchivedTooltipMessage} from 'analysis-tool/AnalysisToolMessages';
import {isAnalysisOutputStepType, isNonRefreshableStepType} from 'analysis-tool/AnalysisTypes';
import getDownstreamSteps from 'analysis-tool/data/getDownstreamSteps';
import isAnalysisRunning from 'analysis-tool/isAnalysisRunning';
import {trackEvent} from 'analytics/Analytics';
import isArchived from 'archive/isArchived';
import {ActionMethod, ActionOrigin, ANALYSIS_TOOL_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {PipelineGraphQL, PipelineStep} from 'pipelines/PipelineTypes';
import {getInputStepDatas} from 'pipelines/StepConnectionUtils';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {readableTime} from 'util/DateUtil';

const REFRESH_DATA_BUTTON_TOOLTIP_ENABLED =
  'Refresh data sources. Data may change based on your access permissions.';
const REFRESH_DATA_BUTTON_TOOLTIP_NO_REFRESHABLE_SOURCE_STEPS = 'No refreshable steps in this analysis';
const REFRESH_DATA_BUTTON_TOOLTIP_STEPS_RUNNING = "Can't refresh while steps are running";
const REFRESH_DATA_BUTTON_TOOLTIP_OUTPUTS = "Can't refresh source steps with generated outputs";
const REFRESH_DATA_BUTTON_TOOLTIP_NO_PERMISSIONS = 'You do not have permissions to modify this analysis.';

const REFRESH_DATA_BUTTON_TEXT = 'Refresh data';
const REFRESH_DATA_BUTTON_TEXT_WHILE_REFRESHING = 'Refreshing data...';

interface RefreshDataButtonDisabledReason {
  isDisabled: boolean;
  tooltipText: string;
}

interface UseRefreshDataButtonStateResult {
  isDisabled: boolean;
  tooltipText: string | null;
  buttonText: string;
  handleClick: () => Promise<void>;
}

/**
 * Get all data source steps in the given analysis.
 *
 * These are those steps in the analysis whose step types do not accept any input datas.
 */
function getAllSourceSteps(analysis: PipelineGraphQL): Array<PipelineStep> {
  return analysis.steps.filter((step) => getInputStepDatas(step.stepType).length === 0);
}

/**
 * Get all data source steps in the given analysis that are refreshable.
 */
function getRefreshableDataSourceSteps(analysis: PipelineGraphQL): Array<PipelineStep> {
  return getAllSourceSteps(analysis).filter((step) => !isNonRefreshableStepType(step.stepType));
}

/**
 * Checks if any source steps in the given analysis have output descendants.
 *
 * @param analysis The analysis to check for output steps
 * @returns True if any source steps in the given analysis lead to output steps, false otherwise
 */
function hasAnySourceStepWithOutputDescendants(analysis: PipelineGraphQL): boolean {
  return getAllSourceSteps(analysis).some((sourceStep) => {
    const downstreamSteps = getDownstreamSteps(analysis, sourceStep.id);
    for (const step of downstreamSteps) {
      if (isAnalysisOutputStepType(step.definition.stepType)) {
        return true;
      }
    }
    return false;
  });
}

/**
 * Rerun the given steps within the given analysis.
 * @param analysisId The ID of the analysis where the steps to rerun are
 * @param steps The steps to rerun
 * @returns The result of the rerun steps action
 */
function rerunSteps(analysisId: string, steps: Array<PipelineStep>): ReturnType<typeof addAndUpdateSteps> {
  return addAndUpdateSteps(analysisId, {
    stepsToAdd: [],
    stepsToUpdate: steps.map((step) => ({
      stepId: step.id,
      rerunAfterUpdate: true,
    })),
  });
}

function trackRefreshDataButtonClicked(
  analysis: PipelineGraphQL,
  refreshableDataSourceSteps: Array<PipelineStep>
): void {
  const stepCountsByType = refreshableDataSourceSteps.reduce<
    Partial<Record<PipelineStep['stepType'], number>>
  >((stepCountsByTypeMap, step) => {
    stepCountsByTypeMap[step.stepType] = (stepCountsByTypeMap[step.stepType] ?? 0) + 1;
    return stepCountsByTypeMap;
  }, {});

  const refreshableStepTypeCounts = Object.entries(stepCountsByType).map(([stepType, count]) => ({
    step_type: stepType,
    count,
  }));

  trackEvent(ANALYSIS_TOOL_ANALYTICS_ACTIONS.CLICKED_REFRESH_DATA, {
    action_method: ActionMethod.BUTTON_CLICK,
    action_origin: ActionOrigin.ANALYSIS_TOOL_HEADER,
    pipeline_id: analysis.id,
    refreshable_step_type_counts: refreshableStepTypeCounts,
  });
}

/**
 * Timestamp for when refreshable data sources were last refreshed (*collectively* as an analysis).
 *
 * Uses the *oldest* `modifiedAt` among refreshable source steps: the analysis is only as current
 * as the stalest source, even if other sources were updated more recently.
 *
 * @param analysis The analysis to read execution timestamps from
 * @param refreshableDataSourceSteps Refreshable data source steps to include
 * @returns That collective refresh time as an ISO string, or null if none have timestamps
 */
function getLastRefreshTimestamp(
  analysis: PipelineGraphQL,
  refreshableDataSourceSteps: Array<PipelineStep>
): string | null {
  const refreshableIds = new Set(refreshableDataSourceSteps.map((step) => step.id));
  const timestamps = analysis.execution.steps
    .filter((step) => refreshableIds.has(step.id))
    .map((step) => step.modifiedAt)
    .filter((timestamp): timestamp is string => timestamp != null);
  if (timestamps.length === 0) {
    return null;
  }
  return timestamps.reduce((earliestTimestamp, timestamp) =>
    new Date(timestamp).getTime() < new Date(earliestTimestamp).getTime() ? timestamp : earliestTimestamp
  );
}

/**
 * Appends a line to the given tooltip text indicating the last refresh timestamp, if applicable.
 *
 * @param baseTooltip The base tooltip text
 * @param isDisabled Whether the refresh button is disabled
 * @param lastRefreshTimestamp The last refresh timestamp as an ISO string, or null
 * @returns The tooltip text with the last refresh timestamp line, if applicable
 */
function withOptionalLastRefreshedLine(
  baseTooltip: string,
  isDisabled: boolean,
  lastRefreshTimestamp: string | null
): string {
  if (isDisabled || lastRefreshTimestamp == null) {
    return baseTooltip;
  }
  return `${baseTooltip}\nLast refreshed: ${readableTime(lastRefreshTimestamp)}`;
}

/**
 * Check whether the refresh button should be disabled for the given analysis, and if so, the reason.
 *
 * @param analysis The analysis to check
 * @returns RefreshDataButtonDisabledReason, which contains:
 *   - isDisabled: boolean indicating whether the refresh button should be disabled
 *   - tooltipText: user-facing string to show in the button's tooltip
 */
function getRefreshButtonDisabledReason(analysis: PipelineGraphQL): RefreshDataButtonDisabledReason {
  // Archiving is handled prior to permissions check because an archived analysis will not be writable.
  // This order of checks ensures a more specific error in the case of an archived analysis.
  if (isArchived(analysis)) {
    return {isDisabled: true, tooltipText: getArchivedTooltipMessage(analysis)};
  }

  // As with other similar buttons in the analysis header, this message should not actually be shown to the
  // user, because options to modify the analysis should be hidden if the analysis is read-only. This check is
  // still included for completeness.
  if (!analysis.permissions.writable) {
    return {
      isDisabled: true,
      tooltipText: REFRESH_DATA_BUTTON_TOOLTIP_NO_PERMISSIONS,
    };
  }

  if (getRefreshableDataSourceSteps(analysis).length === 0) {
    return {isDisabled: true, tooltipText: REFRESH_DATA_BUTTON_TOOLTIP_NO_REFRESHABLE_SOURCE_STEPS};
  }

  if (isAnalysisRunning(analysis)) {
    return {isDisabled: true, tooltipText: REFRESH_DATA_BUTTON_TOOLTIP_STEPS_RUNNING};
  }

  if (hasAnySourceStepWithOutputDescendants(analysis)) {
    return {
      isDisabled: true,
      tooltipText: REFRESH_DATA_BUTTON_TOOLTIP_OUTPUTS,
    };
  }

  return {
    isDisabled: false,
    tooltipText: REFRESH_DATA_BUTTON_TOOLTIP_ENABLED,
  };
}

function useRefreshDataButtonState(analysis: PipelineGraphQL): UseRefreshDataButtonStateResult {
  const [isRefreshRequestRunning, setIsRefreshRequestRunning] = useState(false);
  const buttonDisabledState = useMemo(() => getRefreshButtonDisabledReason(analysis), [analysis]);
  const lastRefreshTimestamp = useMemo(
    () => getLastRefreshTimestamp(analysis, getRefreshableDataSourceSteps(analysis)),
    [analysis]
  );

  const handleClick = useCallback(async (): Promise<void> => {
    if (isRefreshRequestRunning || buttonDisabledState.isDisabled) {
      return;
    }

    setIsRefreshRequestRunning(true);
    const refreshableDataSourceSteps = getRefreshableDataSourceSteps(analysis);
    trackRefreshDataButtonClicked(analysis, refreshableDataSourceSteps);
    try {
      await rerunSteps(analysis.id, refreshableDataSourceSteps);
    } finally {
      setIsRefreshRequestRunning(false);
    }
  }, [analysis, buttonDisabledState.isDisabled, isRefreshRequestRunning]);

  if (isRefreshRequestRunning) {
    return {
      isDisabled: true,
      tooltipText: null,
      buttonText: REFRESH_DATA_BUTTON_TEXT_WHILE_REFRESHING,
      handleClick,
    };
  }

  return {
    ...buttonDisabledState,
    tooltipText: withOptionalLastRefreshedLine(
      buttonDisabledState.tooltipText,
      buttonDisabledState.isDisabled,
      lastRefreshTimestamp
    ),
    buttonText: REFRESH_DATA_BUTTON_TEXT,
    handleClick,
  };
}

export interface RefreshDataButtonProps {
  analysis: PipelineGraphQL;
}

export default function RefreshDataButton({analysis}: RefreshDataButtonProps): JSX.Element {
  const {buttonText, handleClick, isDisabled, tooltipText} = useRefreshDataButtonState(analysis);

  return (
    // Deprecated ui-library tooltip used intentionally to align with other buttons in the same header
    <Tooltip text={tooltipText}>
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        dataTestAttrs={{component: 'RefreshDataButton', element: 'root'}}
        icon="refresh"
        size={ButtonSize.SMALL}
        text={buttonText}
        disabled={isDisabled}
        onClick={handleClick}
      />
    </Tooltip>
  );
}
